import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Flame, Star, Sprout, TrendingUp, Share2, ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const PRIO = {
  HIGH: { icon: Flame, label: "High Priority", cls: "text-critical bg-critical-soft border-critical/20" },
  MEDIUM: { icon: Star, label: "Medium Priority", cls: "text-attention bg-attention-soft border-attention/20" },
  GROWTH: { icon: TrendingUp, label: "Growth", cls: "text-info bg-info-soft border-info/20" },
  VISIBILITY: { icon: Share2, label: "Visibility", cls: "text-gold-600 bg-gold-100 border-gold-500/30" },
  MAINTENANCE: { icon: Sprout, label: "Maintenance", cls: "text-success bg-success-soft border-success/20" },
};

// Presentation-only friendlier badge word for maintenance-tier items (freshness
// = refresh/confirm). Keyed by action type so we never hardcode one label for all.
// Deterministic priority/tier/rank semantics are untouched.
const MAINTENANCE_DISPLAY = {
  LISTING_FRESHNESS: "Refresh",
  BUYER_NEED_FRESHNESS: "Refresh",
};

function badgeLabel(o, p) {
  if (o.priority === "MAINTENANCE") return MAINTENANCE_DISPLAY[o.type] || "Keep Current";
  return p.label;
}

// Presentation-only clarity for the collaboration "Continue" action label
// (handles aggregated "Continue (2)" too). CTA destination/type unchanged.
function displayAction(text, o) {
  const t = text || "";
  if (o.type === "COLLABORATION_ACTION" && /^Continue\b/.test(t)) {
    return t.replace(/^Continue\b/, "Continue Collaboration");
  }
  return t;
}

export const SalesCoachModule = ({ embedded = false }) => {
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, failed: false });

  useEffect(() => {
    let alive = true;
    api.get("/ai/sales-coach")
      .then(({ data }) => { if (alive) setState({ loading: false, data, failed: false }); })
      .catch(() => { if (alive) setState({ loading: false, data: null, failed: true }); });
    return () => { alive = false; };
  }, []);

  const { loading, data, failed } = state;
  if (failed) return null; // never break the dashboard if coaching is unavailable

  return (
    <section data-testid="sales-coach" className={`${embedded ? "" : "mt-5 "}rounded-2xl border border-navy/12 bg-gradient-to-br from-navy/[0.05] via-info-soft/25 to-transparent p-4 shadow-[0_12px_32px_-20px_rgba(15,44,89,0.20)]`}>
      <div className="flex items-center gap-2 pb-3 mb-1 border-b border-navy/10">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-navy/12 text-navy"><Sparkles className="h-4 w-4" /></span>
        <div>
          <h2 className="font-display text-lg font-semibold text-navy leading-tight">PropertyConnect Sales Coach</h2>
          <p className="text-xs text-slate-500" data-testid="sales-coach-headline">
            {loading ? "Finding your best actions…" : (data?.headline || "Your Top 3 Actions Today")}
          </p>
        </div>
      </div>

      {loading && (
        <div className="mt-3 space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-14 rounded-xl bg-secondary/60 animate-pulse" />)}</div>
      )}

      {!loading && data && data.opportunities?.length === 0 && (
        <div data-testid="sales-coach-empty" className="mt-3 rounded-xl border border-border bg-white p-4 text-center">
          <p className="text-sm font-semibold text-slate-800">You're all caught up.</p>
          <p className="text-xs text-slate-500 mt-1">{data.empty_state}</p>
        </div>
      )}

      {!loading && data && data.opportunities?.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="sales-coach-list">
          {data.opportunities.map((o, i) => {
            const p = PRIO[o.priority] || PRIO.MEDIUM; const PIcon = p.icon;
            return (
              <div key={o.key} data-testid={`coach-card-${i}`} className="rounded-xl border border-border bg-white px-3.5 py-3">
                <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${p.cls}`}>
                  <PIcon className="h-3 w-3" /> {badgeLabel(o, p)}
                </span>
                <div className="font-semibold text-slate-900 text-sm leading-snug mt-2">{displayAction(o.action, o)}</div>
                <p className="text-[13px] text-slate-500 mt-1 break-words leading-snug" data-testid={`coach-why-${i}`}>{o.why}</p>
                <button type="button" data-testid={`coach-cta-${i}`} onClick={() => nav(o.cta_destination)}
                  className="mt-2.5 inline-flex items-center justify-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 transition-opacity">
                  {displayAction(o.cta_label, o)} <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
          <p className="text-[11px] text-slate-400 pt-0.5">
            {data.coaching_source === "ai" ? "AI-assisted coaching over your deterministic opportunities." : "Prioritized by your PropertyConnect Opportunity Engine."}
          </p>
        </div>
      )}
    </section>
  );
};
